/* Machine heartbeat — the always-on box (Forge's Claude Code runtime) beats
   /api/heartbeat every ~60s. Shown two ways:
     <HeartbeatChip> — a slim one-line pill for the Command surface.
     <HeartbeatTile> — a fuller card for the Crew surface.
   Both read /api/heartbeat (polled ~30s → real "last seen Ns ago") and degrade
   honestly: a never-seen box reads "not connected" (muted, with the connect
   hint); a placeholder source flags "sample". Color is never the only signal —
   every state carries a word + dot (WCAG 2.2 AA). */
import type { CSSProperties } from 'react';
import { useContract } from '../lib/api';
import { deriveHeartbeat, primaryHeartbeat, type HeartbeatView } from '../lib/selectors';
import { integrationFor } from '../lib/flags';
import { Badge } from '../ds';
import { Panel } from './ui';

const mono: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' };

/** Tone → soft/line/ink surface colors (cyan online, coral offline, neutral never). */
function toneSkin(t: HeartbeatView['tone']) {
  if (t === 'cyan') return { soft: 'var(--accent-soft)', line: 'var(--accent-line)', ink: 'var(--accent-ink)' };
  if (t === 'danger') return { soft: 'var(--danger-soft)', line: 'var(--danger-line)', ink: 'var(--danger)' };
  return { soft: 'var(--surface2)', line: 'var(--border)', ink: 'var(--muted)' };
}

const dot = (color: string, live: boolean, size = 8): CSSProperties => ({
  width: size, height: size, borderRadius: '50%', background: color, flex: 'none',
  boxShadow: live ? `0 0 7px ${color}` : 'none',
  animation: live ? 'ct-pulse 2s ease-in-out infinite' : 'none',
});

/* ---------------------------------------------------------------- chip */

export function HeartbeatChip() {
  const q = useContract('heartbeat', { pollMs: 30_000 });
  // Loading: the first fetch hasn't landed. Render a neutral "checking…" — never
  // paint a false "not connected" (the four-state rule; the empty-data default
  // synthesizes a 'never' row that would otherwise read as authoritative).
  const loading = q.state === 'loading' && !q.data;
  const v = primaryHeartbeat(q.data ?? []);
  const sample = q.isPlaceholder;
  const skin = loading ? toneSkin('muted') : toneSkin(v.tone);
  const word = loading ? 'checking…' : v.word;
  return (
    <span
      role="status"
      // aria-label carries STATE only (never the ticking "Ns ago") so the live
      // region announces on a real status change, not on every 30s poll.
      aria-label={loading ? 'Forge status, checking' : `Forge ${v.word}${sample ? ' — sample data' : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 30, padding: '4px 12px',
        background: skin.soft, border: `1px solid ${skin.line}`, borderRadius: 'var(--radius-pill)',
        fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--ink)', minWidth: 0,
      }}
    >
      <span aria-hidden="true" style={dot(loading ? 'var(--muted)' : v.dotColor, !loading && v.online)} />
      <span style={{ fontWeight: 600 }}>{v.agent}</span>
      <span aria-hidden="true" style={{ color: 'var(--border)' }}>·</span>
      <span style={{ color: skin.ink, fontWeight: !loading && v.state === 'offline' ? 700 : 500 }}>{word}</span>
      {!loading && v.lastSeen && <span aria-hidden="true" style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>· {v.label}</span>}
      {sample && <Badge tone="warn">sample</Badge>}
    </span>
  );
}

/* ---------------------------------------------------------------- tile */

export function HeartbeatTile() {
  const q = useContract('heartbeat', { pollMs: 30_000 });
  const views = deriveHeartbeat(q.data ?? []);
  const sample = q.isPlaceholder;
  const connect = integrationFor('heartbeat')?.connect;
  const anyOffline = views.some((v) => v.state === 'offline');
  const allNever = views.length > 0 && views.every((v) => v.state === 'never');
  const online = views.filter((v) => v.online).length;

  return (
    <Panel
      label="Machine heartbeat"
      title="// machine heartbeat"
      aside={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {sample && <Badge tone="warn">sample</Badge>}
          {!sample && anyOffline && <Badge tone="danger">offline</Badge>}
          {views.length > 0 && !allNever && (
            <span style={{ ...mono, color: anyOffline ? 'var(--danger)' : 'var(--accent-ink)' }}>
              {online}/{views.length} online
            </span>
          )}
        </div>
      }
    >
      {views.length === 0 ? (
        <div style={{ ...mono, padding: '6px 0' }}>Waiting for the first beat…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {views.map((v) => {
            const skin = toneSkin(v.tone);
            return (
              <div key={v.agent} style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <span aria-hidden="true" style={dot(v.dotColor, v.online, 10)} />
                <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)', flex: 'none' }}>{v.agent}</span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: skin.ink, background: skin.soft, border: `1px solid ${skin.line}`, borderRadius: 'var(--radius-pill)',
                    padding: '2px 9px', flex: 'none',
                  }}
                >
                  {v.word}
                </span>
                <span style={{ ...mono, marginLeft: 'auto', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {v.label}
                  {v.host && <span aria-hidden="true"> · </span>}
                  {v.host}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ ...mono, marginTop: 12, lineHeight: 1.5 }}>
        {allNever || sample
          ? `${connect ?? 'Start the heartbeat cron'} · beats every ~60s → offline after 3 min`
          : 'beats every ~60s → offline after 3 min · drop pings #needs-admin'}
      </div>
    </Panel>
  );
}
